/**
 * 이상한 방 - 열 장 스택 (순수 DOM)
 *
 * 근거 문서: 로드맵 확정안 3.3절(열 장은 낮은 등급부터, 최고 등급은 마지막에 풀 연출) · 컨셉시트 3절 리빌("마지막이 가장 크게")
 *
 * `ui/envelope.ts`에서 갈라져 나왔다(7절 M3 기록이 예고한 이음매). 열 장에서 주인공이 아닌 **아홉 장**의 작은
 * 플레이트가 이 파일의 전부다 - 어떤 순서로 놓는가(`revealOrder`), 개봉이 끝난 한 장을 켜기(`commit`), 전부 켜기.
 *
 * ## 미리 놓고 뒤집는 것이 아니라, 개봉될 때마다 켠다 (P5)
 *
 * 전에는 아홉 장이 이 격자에서 **빠르게 뒤집히는 것 자체가 연출**이었고, 그래서 `flipNext`가 "다음 장을
 * 넘겨라"를 받아 넘긴 뒤 그 장의 등급을 돌려줬다(화면이 그 값으로 다음 장까지의 간격을 정했다). 지금은
 * 아홉 장도 큰 카드 자리에서 한 장씩 개봉하고, 개봉이 끝난 장만 여기로 내려앉는다 - 그 순간 화면은 이미
 * 등급을 알고 있으므로 돌려줄 값이 없다. 그래서 `commit(index)`이고, 반환형이 사라진 만큼 스택이 단순해졌다.
 *
 * **간격은 여전히 여기 없다.** 한 장의 길이는 `envelopeCeremony.ts`의 `stepRevealMs`가 정하고, 소리도 내지
 * 않는다 - 언제 무엇이 울리는가는 화면의 몫이다.
 *
 * 카드의 모양(`EnvelopeCardView`, `envelopeCard.ts`)을 통째로 받지 않고 `draw`의 등급·산출물만 본다(`StackCard`). 아홉 장의
 * 작은 플레이트가 아는 것은 그 둘뿐이고, 회계 필드(`refundPins` 등)에 기대지 않아야 카드의 모양이 바뀌어도 스택은 안 흔들린다.
 * `envelopeCard`에서 가져오는 것은 문구의 원본 `rewardLabel` 하나다 - 조각끼리 나누는 유일한 의존이다(`envelopePhases.test.ts`).
 */

import type { DrawResult } from '../game/economy/envelope'
import { gradeNameKey, gradeRank, type Grade } from '../game/economy/envelopeTable'
import { t } from '../i18n'

import { createElement, setText } from './dom'
import { rewardLabel } from './envelopeCard'

import './envelopeStack.css'

/* --- 타입 ---------------------------------------------------------------------- */

/** 스택이 카드에서 읽는 것. `EnvelopeCardView`가 그대로 들어와도 맞는 부분집합이다(머리말). */
export type StackCard = Readonly<{ draw: Pick<DrawResult, 'grade' | 'reward'> }>

export type EnvelopeStack = Readonly<{
  /** `.ev-stack`. 본문에 붙이는 것은 화면의 몫이다. */
  root: HTMLElement
  /**
   * 낮은 등급 카드들(주인공을 뺀 나머지)의 **빈 칸**을 짓는다. 비어 있으면 감춘다.
   *
   * 칸은 지어지되 **등급도 글자도 아직 없다.** 전에는 여기서 `data-grade`와 이름을 다 앉히고
   * `opacity: 0`으로만 가렸는데, 그건 가린 것이지 없는 것이 아니다 - 아홉 장의 결과가 첫 프레임부터
   * DOM에 있었고 테두리 색까지 등급마다 달랐다. 지금은 `commit`이 그 둘을 함께 앉힌다.
   */
  reset: (lower: readonly StackCard[]) => void
  /** 이미 켜진 칸의 글자만 다시 쓴다(언어 전환). 아직 안 열린 칸은 비어 있는 채로 둔다. */
  paintTexts: (lower: readonly StackCard[]) => void
  /**
   * 개봉이 끝난 `index`번째(0부터) 장을 켠다. 없는 자리면 아무 일도 하지 않는다 -
   * 몇 장까지 왔는가를 세는 것은 연출을 쥔 화면이고, 스택은 그 눈금을 두 벌 갖지 않는다.
   */
  commit: (index: number, entry: StackCard) => void
  /** 전부 켠다(건너뛰기 · 움직임 줄이기). */
  flipAll: (lower: readonly StackCard[]) => void
}>

/* --- 순수 계산 ------------------------------------------------------------------ */

/**
 * 열 장의 연출 순서. **낮은 등급부터, 같은 등급은 뽑은 순서대로.** 최고 등급이 마지막에 오는 것이 규칙이고
 * (리빌의 "마지막이 가장 크게"), 안정 정렬이라야 같은 뷰가 두 번 그려질 때 순서가 같다.
 */
export function revealOrder<T extends StackCard>(cards: readonly T[]): readonly T[] {
  return [...cards].sort((a, b) => gradeRank(a.draw.grade) - gradeRank(b.draw.grade))
}

/* --- 본체 ---------------------------------------------------------------------- */

/** 스택을 만든다. 처음에는 감춰져 있다 - 한 장짜리 뷰가 더 흔하다. */
export function createEnvelopeStack(): EnvelopeStack {
  const stack = createElement('div', 'ev-stack')
  stack.hidden = true

  /**
   * 미니 플레이트가 입고 있는 등급. `dataset`이 원본이라 여기서 값을 만들지 않는다 -
   * 모르는 값이면 가장 낮은 등급으로 눕힌다(튀지 않는 쪽이 안전한 기본값이다).
   */
  function gradeOf(mini: HTMLElement): Grade {
    const value = mini.dataset.grade
    return value === 'uncommon' || value === 'rare' || value === 'legend' ? value : 'common'
  }

  /** 낮은 등급 카드들. 문서 순서 = 연출 순서다. */
  function minisOf(): HTMLElement[] {
    return [...stack.querySelectorAll<HTMLElement>('.ev-mini')]
  }

  /** 칸 하나에 적히는 말. 등급 약어 + 산출물 한 줄이다. */
  function label(entry: StackCard): string {
    return `${t(gradeNameKey(entry.draw.grade))} ${rewardLabel(entry.draw.reward)}`
  }

  /** 칸 하나를 켠다. 등급·글자·불이 **한 순간에** 앉는다 - 셋 중 하나라도 먼저 앉으면 그게 곧 예고다. */
  function light(mini: HTMLElement, entry: StackCard): void {
    mini.dataset.grade = entry.draw.grade
    setText(mini, label(entry))
    mini.classList.add('is-on')
  }

  return {
    root: stack,

    reset(lower: readonly StackCard[]): void {
      stack.replaceChildren()
      stack.hidden = lower.length === 0
      // 칸만 짓는다. 등급도 글자도 `commit`의 몫이다(위 머리말).
      for (let i = 0; i < lower.length; i += 1) stack.append(createElement('span', 'ev-mini'))
    },

    paintTexts(lower: readonly StackCard[]): void {
      minisOf().forEach((mini, index) => {
        const entry = lower[index]
        // 아직 안 열린 칸은 건드리지 않는다 - 여기서 이름을 쓰면 언어를 바꾸는 것만으로 앞을 볼 수 있다.
        if (entry === undefined || !mini.classList.contains('is-on')) return
        setText(mini, label(entry))
      })
    },

    commit(index: number, entry: StackCard): void {
      const mini = minisOf()[index]
      if (mini === undefined) return
      light(mini, entry)
      // 가장 낮은 등급보다 위면 내려앉으며 한 번 튄다. 튐(220ms)은 어느 등급의 한 장보다도 짧아
      // 다음 장에 잘리지 않는다 - 그 상한을 `envelope.test.ts`가 CSS에서 읽어 표와 맞댄다.
      if (gradeOf(mini) !== 'common') mini.classList.add('is-beat')
    },

    flipAll(lower: readonly StackCard[]): void {
      minisOf().forEach((mini, index) => {
        const entry = lower[index]
        if (entry !== undefined) light(mini, entry)
      })
    },
  }
}
